import {
  LoadingSpinner,
  LocalNavigationProps,
} from '@zeiss/beyond-online-react';
import { LocalNavigation, PageHeader } from '@zeiss/lib-components';
import { useTranslation } from 'react-i18next';
import { Outlet, useMatch, useParams } from 'react-router-dom';

import {
  ApplicationListActions,
  OutletContextType,
  useApplicationListAuthorization,
} from './';
import { useApplicationList } from 'src/hooks/api';
import { StandardRouteParams } from 'src/types';

export const ApplicationList = () => {
  const { t } = useTranslation(['common']);
  const { applicationListId } = useParams<StandardRouteParams>();
  const match = useMatch('/tenant-management/:id/:tab');

  const {
    can,
    isError: isErrorEvaluations,
    isPending: isPendingEvaluations,
  } = useApplicationListAuthorization(applicationListId);

  const { data, error, isPending } = useApplicationList(applicationListId, {
    // Only fetch once the user is confirmed authorized
    enabled: !isPendingEvaluations && !isErrorEvaluations,
  });

  if (isPending || isPendingEvaluations) {
    return (
      <LoadingSpinner label={t('common:loadingFeedback.loading')} size="xl" />
    );
  }

  if (error || isErrorEvaluations) {
    return <div>Error loading data</div>;
  }

  const localNavigationLinks: LocalNavigationProps['links'] = [
    {
      href: '',
      title: t('common:nav.details'),
    },
    {
      href: 'tenants',
      title: t('common:glossary.tenant_other'),
    },
    {
      href: 'applications',
      title: t('common:glossary.application_other'),
    },
  ];

  const tabIndex = localNavigationLinks.findIndex(
    ({ href }) => href === match?.params.tab,
  );

  const activeIndex = tabIndex === -1 ? 0 : tabIndex;

  const canCreateTenant = can(ApplicationListActions.CREATE_TENANT);
  const canDelete = can(ApplicationListActions.DELETE);
  const canUpdateApplications = can(ApplicationListActions.UPDATE_APPLICATIONS);
  const canUpdateManagers = can(ApplicationListActions.UPDATE_MANAGERS);
  const canUpdateManagingApplications = can(
    ApplicationListActions.UPDATE_MANAGING_APPLICATIONS,
  );
  const canUpdateName = can(ApplicationListActions.UPDATE_NAME);
  const canUpdateOwners = can(ApplicationListActions.UPDATE_OWNERS);

  const {
    id,
    managers: applicationListManagers = [],
    managingApplications: managingApplications = [],
    name: applicationListName,
    owners: applicationListOwners = [],
  } = data;

  return (
    <>
      <PageHeader
        eyebrow={t('common:glossary.applicationList')}
        headline={applicationListName}
      />

      <LocalNavigation activeIndex={activeIndex} links={localNavigationLinks} />

      <Outlet
        context={
          {
            applicationListId: id,
            applicationListManagers,
            applicationListName,
            applicationListOwners,
            canCreateTenant,
            canDelete,
            canUpdateApplications,
            canUpdateManagers,
            canUpdateManagingApplications,
            canUpdateName,
            canUpdateOwners,
            managingApplications,
          } satisfies OutletContextType
        }
      />
    </>
  );
};
